import React from "react";
import {ToolType} from "../models/ToolType";
import {FlexCol, FlexRow} from "./generic/Containers";
import {BCHeader2, BCHeader3, Body1, Body2, Body3, Subtitle1, Subtitle2} from "./generic/BCHeaders";
import type { IconType } from "react-icons";
import {Course} from "../models/Course";
import "../css/DashboardPreviews.css"
import {Resource} from "../models/Resource";
import {Reference} from "../models/Reference";
import {Game} from "../models/Game";
import {Challenge} from "../models/Challenge";
import {Article} from "../models/Article";
import {Interview} from "../models/Interview";
import {BCIcon, ICONS} from "./generic/BCIcons";
import {BCTertiaryButton} from "./generic/BCButtons";
import {BCTagList, TAGS, Tag, TAG_COLORS} from "./generic/BCTag";
import {useGlobalState} from "../GlobalState";
import {getChallengeProgress, getCourseProgress, hasUserCompletedCourse, hasUserCompletedInterview} from "../util/UserProgressHelper";

interface PreviewIcon {
    text: string;
    icon: IconType;
}

interface ProgressBadgeProps {
    isCompleted: boolean;
    progressPercentage?: number;
    showPercentage?: boolean;
    completedText?: string;
    position?: 'top-left' | 'top-right';
}

const ProgressBadge: React.FC<ProgressBadgeProps> = ({ 
    isCompleted, 
    progressPercentage = 0, 
    showPercentage = true,
    completedText = "Complete",
    position = 'top-right'
}) => {
    if (!isCompleted && progressPercentage === 0) return null;

    const positionStyles = position === 'top-left' 
        ? { top: '0.5em', left: '0.5em' }
        : { top: '0.5em', right: '0.5em' };

    return (
        <div style={{ 
            position: 'absolute', 
            ...positionStyles,
            backgroundColor: 'var(--primary-blue)',
            color: 'white',
            padding: '0.25em 0.5em',
            borderRadius: '0.5em',
            fontSize: '0.75em',
            fontWeight: 'bold',
            zIndex: 10
        }}>
            {isCompleted ? (
                <FlexRow gap={0.25} align="center">
                    <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ fontSize: '0.9em' }} />
                    <span>{showPercentage ? `${progressPercentage}%` : completedText}</span>
                </FlexRow>
            ) : (
                showPercentage && `${progressPercentage}%`
            )}
        </div>
    );
};

export const CoursePreview: React.FC<{ course: Course, setSelectedCourse: any }> = ({ course, setSelectedCourse }) => {
    const { isAuthenticated, userProgressData } = useGlobalState();
    
    const icons: PreviewIcon[] = [
        { icon: ICONS.LESSONS, text: `${course.numberOfLessons} Lessons` },
        { icon: ICONS.CLOCK, text: `${course.time}` }
    ];

    // Get course completion status and progress
    const isCompleted = isAuthenticated && course.trackingId 
        ? hasUserCompletedCourse(userProgressData, course.trackingId)
        : false;
    
    const progressPercentage = isAuthenticated && course.trackingId 
        ? getCourseProgress(userProgressData, course.trackingId)
        : 0;

    return (
        <FlexCol 
            borderRadius={1} 
            gap={.5} 
            padding={1} 
            className="preview-container" 
            onClick={() => setSelectedCourse(course.navUrl, "courses")}
            style={{
                backgroundColor: isCompleted ? 'var(--success-background, #f8f9fa)' : undefined,
                position: 'relative'
            }}
        >
            <ProgressBadge isCompleted={isCompleted} progressPercentage={progressPercentage} />
            <img src={course.thumbnail} alt={`${course.name} icon`}/>
            <PreviewInfoContainer name={course.name} description={course.description} icons={icons}/>
            {progressPercentage > 0 && progressPercentage < 100 && (
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'var(--progress-bg, #e9ecef)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginTop: '0.5rem'
                }}>
                    <div style={{
                        width: `${progressPercentage}%`,
                        height: '100%',
                        backgroundColor: 'var(--primary-blue)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            )}
            {isCompleted && (
                <FlexRow gap={0.5} align="center" style={{ marginTop: '0.5rem' }}>
                    <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ 
                        color: 'var(--success-color, #28a745)', 
                        fontSize: '1em' 
                    }} />
                    <Body3 text="Course Completed!" style={{
                        color: 'var(--success-color, #28a745)',
                        fontWeight: 'bold',
                        fontSize: '0.85em'
                    }} />
                </FlexRow>
            )}
        </FlexCol>
    );
};

export const VideoPreview: React.FC<{ video: Interview, setSelectedContent: any }> = ({ video, setSelectedContent }) => {
    const { isAuthenticated, userProgressData } = useGlobalState();
    
    const icons: PreviewIcon[] = [
        { icon: ICONS.CLOCK, text: `${video.time}` }
    ];

    // Check if this interview is completed
    const isCompleted = isAuthenticated && video.trackingId 
        ? hasUserCompletedInterview(userProgressData, video.trackingId)
        : false;

    // For videos, it's either 100% (viewed) or 0% (not viewed)
    const progressPercentage = isCompleted ? 100 : 0;

    return (
        <FlexCol 
            borderRadius={1} 
            padding={1} 
            gap={.5} 
            className="preview-container" 
            onClick={() => setSelectedContent(video.navUrl, "interviews")}
            style={{
                backgroundColor: isCompleted ? 'var(--success-background, #f8f9fa)' : undefined
            }}
        >
            <ProgressBadge 
                isCompleted={isCompleted} 
                progressPercentage={progressPercentage} 
                showPercentage={false}
                completedText="Viewed"
            />
            <img src={video.thumbnail} alt={`${video.name} icon`}/>
            <PreviewInfoContainer name={video.name} icons={icons}/>
        </FlexCol>
    );
};

export const ToolPreview: React.FC<{ tool: ToolType, setSelectedTool: any }> = ({ tool, setSelectedTool }) => {

    return (
        <FlexCol borderRadius={1} padding={1} gap={.5} className="preview-container tools" onClick={() => setSelectedTool(tool.navUrl, "tools")}>
            <img src={tool.thumbnail} alt={`${tool.name} icon`}/>
            <PreviewInfoContainer name={tool.name} description={tool.description} icons={[]}/>
            <FlexRow className={"tool-preview-try-now"} alignSelf={"flex-end"} gap={.5} justify={"center"}>
                <Body3 text={"Try Now"}/>
                <BCIcon icon={ICONS.RIGHT_ARROW} />
            </FlexRow>
        </FlexCol>
    );
};


export const ResourcePreview: React.FC<{ resource: Resource, setSelectedResource: any }> = ({ resource, setSelectedResource }) => {
    const icons: PreviewIcon[] = [
        { icon: ICONS.PDF, text: `Downloadable PDF` }
    ];
    return (
        <FlexCol borderRadius={1} padding={1} gap={.5} className="resource-preview" onClick={() => setSelectedResource(resource.navUrl, "resources")}>
            <img src={resource.thumbnail} alt={`${resource.name} icon`}/>
            <PreviewInfoContainer name={resource.name} icons={icons}/>
        </FlexCol>
    );
};

export const ReferencePreview: React.FC<{ reference: Reference, setSelectedResource: any }> = ({ reference, setSelectedResource }) => {
    return (
        <FlexRow borderRadius={1} padding={1} gap={.5} className="reference-preview" onClick={() => setSelectedResource(reference.navUrl, "references")} reverseTablet={true} reverseMobile={true} >
            <img src={reference.thumbnail} alt={`${reference.name} icon`}/>
            <FlexCol pHorizontal={1} gap={1}>
                <BCHeader2 text={reference.name} />
                <Body1 text={reference.description} />
            </FlexCol>
        </FlexRow>
    );
};

export const GamePreview: React.FC<{ game: Game, setSelectedGame: any }> = ({ game, setSelectedGame }) => {
    return (
        <FlexRow borderRadius={1} padding={1} gap={.5} className="reference-preview" onClick={() => setSelectedGame(game.navUrl, "games")} reverseTablet={true} reverseMobile={true} >
            <img src={game.thumbnail} alt={`${game.name} icon`}/>
            <FlexCol pHorizontal={1} gap={1}>
                <BCHeader2 text={game.name} />
                <Body1 text={game.description} />
            </FlexCol>
        </FlexRow>
    );
};

export const ChallengePreview: React.FC<{ challenge: Challenge, setSelectedChallenge: any }> = ({ challenge, setSelectedChallenge }) => {
    const { isAuthenticated, userProgressData } = useGlobalState();
    
    // Helper function to get unique tags from all challenge items
    const getAllTags = (challenge: Challenge): Tag[] => {
        const allTags: Tag[] = [];
        challenge.challengeItems.forEach(item => {
            if (item.tags) {
                allTags.push(...item.tags);
            }
        });
        
        // Remove duplicates by comparing tag titles
        const uniqueTags = allTags.filter((tag, index, self) => 
            index === self.findIndex(t => t.title === tag.title)
        );
        
        return uniqueTags;
    };

    const allTags = getAllTags(challenge);
    
    // Get challenge progress percentage
    const progressPercentage = isAuthenticated && challenge.trackingId 
        ? getChallengeProgress(userProgressData, challenge.trackingId)
        : 0;

    return (
        <FlexRow 
            borderRadius={1} 
            padding={1} 
            gap={.5} 
            className="challenge-preview" 
            onClick={() => setSelectedChallenge(challenge.navUrl, "challenges")}
            style={{ position: 'relative' }}
            reverseMobile={true}
        >
            <img src={challenge.thumbnail} alt={`${challenge.name} icon`}/>
            <FlexCol pHorizontal={1} gap={1} flex={1}>
                <BCHeader2 text={challenge.name} />
                <Body1 text={challenge.description} />
                {allTags.length > 0 && <BCTagList tags={allTags} />}
                {progressPercentage > 0 && (
                    <FlexRow gap={1.5} align="center">
                        <div style={{
                            flex: 1,
                            height: '4px',
                            backgroundColor: 'var(--progress-bg, #e9ecef)',
                            borderRadius: '2px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${progressPercentage}%`,
                                height: '100%',
                                backgroundColor: 'var(--primary-blue)',
                                borderRadius: '2px',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <Body3 text={`${progressPercentage}%`} style={{
                            color: 'var(--primary-blue)',
                            fontWeight: 'bold',
                            fontSize: '0.9em',
                            minWidth: 'fit-content'
                        }} />
                    </FlexRow>
                )}
            </FlexCol>
        </FlexRow>
    );
};

export const BlogPreview: React.FC<{ blog: Article, setSelectedBlog: any }> = ({ blog, setSelectedBlog }) => {
    const icons: PreviewIcon[] = [
        { icon: ICONS.CALENDAR, text: `${blog.date}` }
    ];
    return (
        <FlexCol borderRadius={1} padding={1} gap={.5} className="blog-preview" onClick={() => setSelectedBlog(blog.navUrl, "blog")}>
            <img src={blog.thumbnail} alt={`${blog.name} icon`}/>
            <PreviewInfoContainer name={blog.name} description={blog.description} icons={icons}/>
        </FlexCol>
    );
};

const PreviewInfoContainer: React.FC<{ name: string, description?: string, icons: PreviewIcon[] }> = ({ name, description, icons }) => {
    return (
        <FlexCol gap={1} justify={"space-between"} heightPercent={100}>
            <FlexCol gap={.5}>
                <BCHeader3 className={'preview-title'} text={name} />
                {description && <Body2 className={'preview-subtitle'} text={description} />}
            </FlexCol>
            <FlexRow gap={1}>
                {icons.map((previewIcon, index) => (
                    <FlexRow key={index} borderRadius={.5} gap={.5} className={'course-info-container'} pVertical={.3} pHorizontal={.5} justify={"center"} align={"center"}>
                        <BCIcon icon={previewIcon.icon}/>
                        <Body3 text={previewIcon.text} />
                    </FlexRow>
                ))}
            </FlexRow>
        </FlexCol>
    );
}